import React, {useState, useMemo, useCallback} from "react";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import WarningIcon from "@material-ui/icons/Warning";
import DeleteIcon from "@material-ui/icons/Delete";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import PortfolioReportIssuesDialog from "./portfolio_report_issues_dialog";

import {Uuid} from "common/types";

const styles = {
  root: {
    padding: "2em",
    boxSizing: "border-box",
    maxWidth: "100%",
  },
  headerSection: {
    marginBottom: "1.5em",
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 500,
    marginBottom: "1em",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  issuesContainer: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "4px",
    overflow: "hidden",
  },
  issuesList: {
    padding: 0,
    width: "100%",
  },
  issueItem: {
    padding: ".75em",
  },
  issueText: {
    fontWeight: 400,
  },
  customDivider: {
    height: "1px",
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    margin: 0,
    padding: 0,
  },
  selectControl: {
    width: "100%",
    marginBottom: ".5em",
  },
  warningIcon: {
    color: "#f57c00",
    fontSize: "1rem",
    marginLeft: "8px",
    verticalAlign: "middle",
  },
} as const;

interface Issueset {
  master_id: Uuid;
  name: string;
  remote_client_name?: string;
  is_archived: boolean;
  is_hidden: boolean;
}

interface ContractType {
  name: string;
  issuesets: Issueset[];
}

interface IssuesetItem {
  master_id: Uuid;
  name: string;
  contract_type_name: string;
}

export interface Issue {
  id: number;
  master_id?: Uuid;
  name: string;
  display_name: string;
  issueset_id?: Uuid;
  contract_types: ContractType[];
}

export interface SelectedIssue {
  master_id?: Uuid;
  name: string;
}

export interface IssueItem {
  master_id: Uuid;
}

export type IssueEntry = string | IssueItem;

interface PortfolioReportTabProps {
  bulkReportSettings: {
    used_issueset_master_id: Uuid;
    used_issue_names: IssueEntry[];
  };
  contractTypes: ContractType[];
  issues: Issue[];
  updateProjectPortfolioReportSelectedIssueset: (
    issuesetMasterId: Uuid,
  ) => void;
  updateProjectPortfolioReportIssues: (issueEntries: IssueEntry[]) => void;
}

const formatIssueName = (fullName: string) => {
  if (!fullName) {
    return "";
  }

  if (fullName.includes("/")) {
    return fullName.split("/").pop()?.trim() ?? "";
  }

  return fullName.trim();
};

const getIssuesForIssueset = (
  issuesetMasterId: Uuid,
  issues: Issue[] = [],
): Issue[] => {
  if (!issues.length || !issuesetMasterId) {
    return [];
  }

  return issues.filter((issue: Issue) => {
    if (!issue.contract_types || !Array.isArray(issue.contract_types)) {
      return false;
    }

    return issue.contract_types.some(contractType => {
      if (!contractType.issuesets || !Array.isArray(contractType.issuesets)) {
        return false;
      }

      return contractType.issuesets.some(
        issueset => issueset.master_id === issuesetMasterId,
      );
    });
  });
};

const PortfolioReportTab: React.FC<PortfolioReportTabProps> = ({
  bulkReportSettings,
  contractTypes,
  updateProjectPortfolioReportSelectedIssueset,
  updateProjectPortfolioReportIssues,
  issues,
}) => {
  const issuesetMasterId = bulkReportSettings?.used_issueset_master_id;
  const reportIssues = bulkReportSettings?.used_issue_names || [];

  const [isIssueDialogOpen, setIsIssueDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [pendingIssuesetId, setPendingIssuesetId] = useState<Uuid | "">("");

  const allIssuesets = useMemo(() => {
    if (!contractTypes || !contractTypes.length) {
      return [];
    }

    const seen = new Set<Uuid>();
    const result: IssuesetItem[] = [];

    for (const contractType of contractTypes) {
      const issuesets = contractType?.issuesets || [];

      for (const issueset of issuesets) {
        if (
          !issueset.is_archived &&
          !issueset.is_hidden &&
          !seen.has(issueset.master_id)
        ) {
          seen.add(issueset.master_id);
          result.push({
            master_id: issueset.master_id,
            name: issueset.remote_client_name
              ? `${issueset.name} - ${issueset.remote_client_name}`
              : issueset.name,
            contract_type_name: contractType.name,
          });
        }
      }
    }
    return result;
  }, [contractTypes]);

  const availableIssues = useMemo(
    () =>
      issuesetMasterId ? getIssuesForIssueset(issuesetMasterId, issues) : [],
    [issuesetMasterId, issues],
  );

  const selectedIssues = useMemo(() => {
    if (!Array.isArray(reportIssues)) {
      return [];
    }

    return reportIssues
      .map(issueEntry => {
        if (typeof issueEntry === "string") {
          return {name: issueEntry};
        }
        if (
          typeof issueEntry === "object" &&
          issueEntry !== null &&
          "master_id" in issueEntry
        ) {
          const matchingIssue = issues.find(
            i => i.master_id === issueEntry.master_id,
          );
          if (matchingIssue) {
            return {
              master_id: issueEntry.master_id,
              name: formatIssueName(
                matchingIssue.display_name || matchingIssue.name,
              ),
            };
          }
        }
        return null;
      })
      .filter((item): item is SelectedIssue => item !== null);
  }, [reportIssues, issues]);

  const convertToIssueEntries = useCallback(
    (selectedIssues: SelectedIssue[]): IssueEntry[] => {
      return selectedIssues.map(item => {
        if (item.master_id) {
          return {master_id: item.master_id};
        } else {
          const matchingIssue = availableIssues.find(
            issue =>
              formatIssueName(issue.display_name || issue.name) === item.name,
          );

          if (matchingIssue?.master_id) {
            return {master_id: matchingIssue.master_id};
          } else {
            return item.name;
          }
        }
      });
    },
    [availableIssues],
  );

  const handleIssuesetChange = useCallback(
    (event: React.ChangeEvent<{value: unknown}>) => {
      const newIssuesetId = event.target.value as Uuid;

      if (selectedIssues.length === 0 || newIssuesetId === issuesetMasterId) {
        updateProjectPortfolioReportSelectedIssueset(newIssuesetId);
      } else {
        setPendingIssuesetId(newIssuesetId);
        setIsConfirmDialogOpen(true);
      }
    },
    [
      selectedIssues,
      issuesetMasterId,
      updateProjectPortfolioReportSelectedIssueset,
    ],
  );

  const handleConfirmIssuesetChange = useCallback(() => {
    if (pendingIssuesetId) {
      updateProjectPortfolioReportSelectedIssueset(pendingIssuesetId);
    }
    setIsConfirmDialogOpen(false);
    setPendingIssuesetId("");
  }, [pendingIssuesetId, updateProjectPortfolioReportSelectedIssueset]);

  const handleCancelIssuesetChange = () => {
    setIsConfirmDialogOpen(false);
    setPendingIssuesetId("");
  };

  const handleOpenIssueDialog = () => setIsIssueDialogOpen(true);
  const handleCloseIssueDialog = () => setIsIssueDialogOpen(false);

  const handleRemoveIssue = useCallback(
    (issue: SelectedIssue) => {
      const newSelectedIssues = selectedIssues.filter(item => {
        if (issue.master_id && item.master_id) {
          return item.master_id !== issue.master_id;
        }
        return item.name !== issue.name;
      });

      const issueEntries = convertToIssueEntries(newSelectedIssues);
      updateProjectPortfolioReportIssues(issueEntries);
    },
    [selectedIssues, updateProjectPortfolioReportIssues, convertToIssueEntries],
  );

  const handleSaveFromDialog = useCallback(
    (dialogSelectedIssues: SelectedIssue[]) => {
      const issueEntries = convertToIssueEntries(dialogSelectedIssues);
      updateProjectPortfolioReportIssues(issueEntries);
      setIsIssueDialogOpen(false);
    },
    [updateProjectPortfolioReportIssues, convertToIssueEntries],
  );

  const renderSelectedIssuesList = () => {
    if (selectedIssues.length === 0) {
      return (
        <Typography variant="body1" color="textSecondary">
          No issues selected
        </Typography>
      );
    }

    return (
      <div style={styles.issuesContainer}>
        <List style={styles.issuesList}>
          {selectedIssues.map((issue, index) => (
            <React.Fragment
              key={
                issue.master_id
                  ? `issue-${issue.master_id}`
                  : `name-${issue.name}-${index}`
              }
            >
              <ListItem style={styles.issueItem}>
                <ListItemText
                  primary={
                    <React.Fragment>
                      {issue.name}
                      {!issue.master_id && (
                        <Tooltip title="Issue not found in selected checklist">
                          <WarningIcon style={styles.warningIcon} />
                        </Tooltip>
                      )}
                    </React.Fragment>
                  }
                  style={styles.issueText}
                  primaryTypographyProps={{
                    variant: "body1",
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveIssue(issue)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index < selectedIssues.length - 1 && (
                <div style={styles.customDivider} />
              )}
            </React.Fragment>
          ))}
        </List>
      </div>
    );
  };

  return (
    <div style={styles.root}>
      <div style={styles.headerSection}>
        <FormControl variant="outlined" style={styles.selectControl}>
          <InputLabel id="issueset-select-label">Selected Issueset</InputLabel>
          <Select
            labelId="issueset-select-label"
            id="issueset-select"
            value={issuesetMasterId || ""}
            onChange={handleIssuesetChange}
            label="Selected Issueset"
          >
            {allIssuesets.map(issueset => (
              <MenuItem key={issueset.master_id} value={issueset.master_id}>
                {issueset.name} ({issueset.contract_type_name})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div style={styles.sectionTitle}>
        <Typography variant="h6" component="span">
          Selected Issues
        </Typography>
        <IconButton
          color="primary"
          onClick={handleOpenIssueDialog}
          disabled={!issuesetMasterId}
          aria-label="Add Issues"
          title={issuesetMasterId ? "Add Issues" : "Select an issueset first"}
        >
          <AddIcon />
        </IconButton>
      </div>

      {renderSelectedIssuesList()}

      <PortfolioReportIssuesDialog
        open={isIssueDialogOpen}
        issues={availableIssues}
        selectedIssues={selectedIssues}
        onClose={handleCloseIssueDialog}
        onSave={handleSaveFromDialog}
        formatIssueName={formatIssueName}
      />

      <Dialog
        open={isConfirmDialogOpen}
        onClose={handleCancelIssuesetChange}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Issueset Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Changing the selected issueset will clear all selected issues. Are
            you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelIssuesetChange} color="primary">
            No
          </Button>
          <Button
            onClick={handleConfirmIssuesetChange}
            color="primary"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PortfolioReportTab;
